import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, PenTool, Stamp, Boxes, RefreshCw, Lightbulb, Layers } from "lucide-react";
import { Eyebrow, SectionHeading, FadeUp } from "../components/site/Primitives";
import SceneImage from "../components/site/SceneImage";

const SERVICES = [
  { icon: Layers, title: "Custom kraft sleeves", body: "Print-ready unbleached kraft sleeves sized for bowls, clamshells and trays. Your logo, story and reorder QR — built to ship." },
  { icon: PenTool, title: "Logo printing", body: "1-colour to full-colour print on kraft and bagasse-compatible surfaces. Tight registration, brand-true ink." },
  { icon: Stamp, title: "Branded stickers", body: "Tamper-evident circular and rectangular seals. Matte kraft or premium coated paper finishes." },
  { icon: Boxes, title: "Bulk supply", body: "Container-load imports paired with local stockholding for predictable monthly volumes." },
  { icon: RefreshCw, title: "Repeat monthly orders", body: "Lock in a recurring run that flexes with your demand. No more last-minute packaging panic." },
  { icon: Lightbulb, title: "Packaging consultation", body: "We'll audit your menu, throughput and brand — and recommend the SKUs that actually fit." },
];

const STEPS = [
  { num: "01", title: "Brand & menu audit", body: "We look at what you serve, how it ships and how it should look on a counter or doorstep." },
  { num: "02", title: "Design & sampling", body: "Print proofs and physical samples for the SKUs you select — kraft sleeves, clamshells, stickers." },
  { num: "03", title: "Production run", body: "Approved artwork goes to print. Container-load planning aligned to your launch date." },
  { num: "04", title: "Recurring supply", body: "Monthly drops to your kitchens. Top-up forecasts and stock visibility throughout." },
];

export default function PrivateLabelPage() {
  return (
    <div className="bg-[#0A0A09]">
      <section className="relative pt-12 pb-24 lg:pb-32 kraft-texture overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-40">
          <SceneImage
            scene="private-label-row"
            eager
            className="h-full w-full object-cover"
            alt=""
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A09] via-[#0A0A09]/80 to-transparent" />
        </div>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <FadeUp>
            <Eyebrow>Private label</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="mt-8 font-grotesk text-5xl md:text-6xl lg:text-8xl font-black tracking-[-0.04em] leading-[0.92] text-[#F7F5F0] max-w-5xl">
              Your brand,{" "}
              <span className="font-serif-display italic font-medium text-[#E85D2F]">beautifully</span>{" "}
              wrapped.
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-[#F7F5F0]/75 leading-relaxed font-light">
              For restaurants, supermarket brands, hotels and food chains who want
              their own branded sustainable packaging — without sacrificing
              consistency, lead times or quality.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <Link
              to="/contact?subject=private-label"
              data-testid="pl-hero-cta"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-7 py-4 font-medium hover:bg-[#F47A4A] transition-colors group"
            >
              Create your branded line
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 lg:py-32 bg-[#121211] border-y border-[#F7F5F0]/5">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <SectionHeading
            eyebrow="What we offer"
            title="A full-service private-label studio."
            kicker="Six core services that take you from menu insight to branded crates at the back door."
          />
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.06}>
                <div className="h-full rounded-3xl border border-[#E85D2F]/15 bg-[#0A0A09] p-7 hover:border-[#E85D2F]/40 hover:-translate-y-1 transition-all duration-500">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1A1A18] text-[#E85D2F]">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-grotesk text-xl font-bold text-[#F7F5F0] tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#F7F5F0]/65 leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <SectionHeading eyebrow="How it works" title="From idea to recurring supply, in four steps." />
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <FadeUp key={s.num} delay={i * 0.08}>
                <div className="rounded-3xl border border-[#F7F5F0]/10 bg-[#121211] p-7 h-full">
                  <p className="font-serif-display italic text-[#E85D2F] text-5xl leading-none">
                    {s.num}
                  </p>
                  <h3 className="mt-6 font-grotesk text-lg font-bold text-[#F7F5F0]">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#F7F5F0]/65 leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="rounded-[2rem] bg-[#E85D2F] text-[#0A0A09] p-10 md:p-16 grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <h3 className="font-grotesk text-3xl md:text-5xl font-black tracking-[-0.03em] leading-[0.95]">
                Create your own{" "}
                <span className="font-serif-display italic font-medium">branded</span>{" "}
                packaging line.
              </h3>
              <p className="mt-4 max-w-xl">
                Send a brief and any artwork — we&rsquo;ll come back with sample SKUs, lead times and indicative pricing.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/contact?subject=private-label"
                data-testid="pl-bottom-cta"
                className="inline-flex items-center gap-3 rounded-full bg-[#0A0A09] text-[#F7F5F0] px-7 py-4 font-medium hover:bg-[#1A1A18] transition-colors"
              >
                Brief our studio
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
